import type { ApiErrorBody } from "@plataforma/shared";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const SESSION_EXPIRED_EVENT = "plataforma:session-expired";

/**
 * Cliente da API. Sempre na mesma origem da página, então o cookie de sessão acompanha
 * a requisição e a aplicação é resolvida pelo endereço, sem configuração no frontend.
 */
async function request<Response>(method: string, path: string, body?: unknown): Promise<Response> {
  let response: globalThis.Response;
  try {
    response = await fetch(`/api${path}`, { method, credentials: "same-origin", headers: body === undefined ? undefined : { "content-type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
  } catch {
    throw new ApiError(0, "NETWORK_ERROR", "Sem conexão com o servidor. Confira a internet e tente de novo.");
  }
  if (response.status === 204) return undefined as Response;
  const payload: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    if (response.status === 401 && !path.startsWith("/auth/")) window.dispatchEvent(new Event(SESSION_EXPIRED_EVENT));
    throw new ApiError(response.status, error?.code ?? "UNKNOWN", error?.message ?? "Algo deu errado. Tente de novo.", error?.details);
  }
  return payload as Response;
}

export const api = {
  get: <Response>(path: string) => request<Response>("GET", path),
  post: <Response>(path: string, body?: unknown) => request<Response>("POST", path, body ?? {}),
  put: <Response>(path: string, body: unknown) => request<Response>("PUT", path, body),
  del: <Response>(path: string) => request<Response>("DELETE", path),
};

export const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : "Algo deu errado. Tente de novo.");

export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== null && value !== "") search.set(key, String(value));
  const text = search.toString();
  return text ? `?${text}` : "";
}
